import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';


const ProfileForm = ({ user, onSave }) => {
  // Validation helpers
  const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚüÜ\s'-]{1,25}$/;
  const validateName = (value) => {
    if (!value.trim()) return 'This field is required.';
    if (value.length > 25) return 'Maximum length is 25 characters.';
    if (!nameRegex.test(value)) return 'Only letters allowed. No numbers or special characters.';
    return '';
  };

  const safeUser = user || { firstName: '', lastName: '' };
  const [editMode, setEditMode] = useState(false);
  const [editFields, setEditFields] = useState({ firstName: safeUser.firstName, lastName: safeUser.lastName });
  const [displayUser, setDisplayUser] = useState(safeUser);

  const firstNameError = validateName(editFields.firstName);
  const lastNameError = validateName(editFields.lastName);
  const hasErrors = !!firstNameError || !!lastNameError;

  React.useEffect(() => {
    const safeUser = user || { firstName: '', lastName: '' };
    setEditFields({ firstName: safeUser.firstName, lastName: safeUser.lastName });
    setDisplayUser(safeUser);
  }, [user]);

  const handleEdit = () => {
    setEditFields({ firstName: displayUser.firstName, lastName: displayUser.lastName });
    setEditMode(true);
  };

  const handleFieldChange = (e) => {
    setEditFields({ ...editFields, [e.target.name]: e.target.value });
  };

  const [modalOpen, setModalOpen] = useState(false);

  const handleSave = () => {
    if (onSave) {
      onSave(editFields);
    }
    setDisplayUser({ ...displayUser, ...editFields });
    setEditMode(false);
    setModalOpen(false);
  };

  const handleShowModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleCancel = () => {
    setEditFields({ firstName: displayUser.firstName, lastName: displayUser.lastName });
    setEditMode(false);
  };

  return (
    <>
      <h2>Profile</h2>
      <div style={{ marginBottom: 12 }}>
        <strong>Name:</strong>{' '}
        {editMode ? (
          <div>
            <TextField
              name="firstName"
              value={editFields.firstName}
              onChange={handleFieldChange}
              size="small"
              variant="outlined"
              style={{ width: 180 }}
              error={!!firstNameError}
              helperText={firstNameError}
              inputProps={{ maxLength: 25 }}
            />
          </div>
        ) : (
          displayUser.firstName
        )}
      </div>
      <div style={{ marginBottom: 12 }}>
        <strong>Last Name:</strong>{' '}
        {editMode ? (
          <div>
            <TextField
              name="lastName"
              value={editFields.lastName}
              onChange={handleFieldChange}
              size="small"
              variant="outlined"
              style={{ width: 180 }}
              error={!!lastNameError}
              helperText={lastNameError}
              inputProps={{ maxLength: 25 }}
            />
          </div>
        ) : (
          displayUser.lastName
        )}
      </div>
      <div style={{ marginBottom: 12 }}>
        <strong>Email:</strong> {displayUser.email}
      </div>
      {editMode && (
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleShowModal}
            size="small"
            disabled={hasErrors}
          >
            Save
          </Button>
          <Button variant="outlined" onClick={handleCancel} size="small">
            Cancel
          </Button>
        </div>
      )}
      {!editMode && (
        <div style={{ position: 'absolute', top: 16, right: 16 }}>
          <IconButton aria-label="edit" onClick={handleEdit} size="small">
            <EditIcon />
          </IconButton>
        </div>
      )}

      <Dialog open={modalOpen} onClose={handleCloseModal}>
        <DialogTitle>Confirm Profile Update</DialogTitle>
        <DialogContent>
          <div style={{ maxWidth: 350 }}>
            This action will also update your Origin (Okta) account.
            Are you sure you want to continue?
            <br /><br />
            <span style={{ color: '#ea922eff', fontWeight: 500 }}>
              The Okta account may take up to 10 minutes to update.
            </span>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSave} color="primary" variant="contained">Yes</Button>
          <Button onClick={handleCloseModal} color="secondary" variant="outlined">No</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ProfileForm.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  onSave: PropTypes.func,
};

export default ProfileForm;
